import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Icon,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { numberWithCommas } from "../../../../utils/formatMoney";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * Consumed by every section component to know whether we are on a mobile
 * viewport.  When true, sections must:
 *   - skip the "Initial Values" middle column entirely
 *   - render label (xs=12) above field (xs=12) instead of side-by-side
 */
export const SectionMobileContext = React.createContext<boolean>(false);

/**
 * When true, the SectionAccordion suppresses its own header (icon + title +
 * dollar total) because MobileSectionView's parent (MobileShell AppBar)
 * already provides the same information, and skips the pt={8} top padding.
 */
export const SectionHeaderlessContext = React.createContext<boolean>(false);

export interface SectionAccordionProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  total: number;
  children: React.ReactNode;
  /**
   * When true, forces the mobile layout regardless of the current viewport
   * width.  Use this when the section is rendered inside MobileShell where
   * useMediaQuery may not reflect the narrow effective width due to SSR
   * defaults or container constraints.
   */
  forceMobile?: boolean;
}

export function SectionAccordion({
  id,
  title,
  icon,
  total,
  children,
  forceMobile,
}: SectionAccordionProps) {
  const theme = useTheme();
  const mediaQueryMobile = useMediaQuery(theme.breakpoints.down("md"));
  // forceMobile wins if explicitly set; otherwise fall back to media query
  const isMobile = forceMobile !== undefined ? forceMobile : mediaQueryMobile;
  const headerless = React.useContext(SectionHeaderlessContext);
  const [expanded, setExpanded] = React.useState(true);

  return (
    <SectionMobileContext.Provider value={isMobile}>
      <Box pt={headerless ? 0 : 3} id={id} sx={{ overflow: "hidden" }}>
        <Paper
          elevation={0}
          sx={isMobile && !headerless ? { borderRadius: 2, overflow: "hidden", border: "0.5px solid", borderColor: "divider" } : undefined}
        >
          {!headerless && (
            <>
              <Accordion elevation={0} expanded={expanded} onChange={() => setExpanded(prev => !prev)}
                sx={{
                  border: "0.5px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { margin: 0 },
                }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.03)",
                    "& .MuiAccordionSummary-root": { borderBottom: "none" },
                    borderBottom: expanded ? "0.5px solid" : "none",
                    borderColor: "rgba(0,0,0,0.12)",
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      flex: 1,
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 0.5, sm: 0 },
                    }}
                  >
                    <Stack direction="row" spacing={2}>
                      <Icon fontSize="large" sx={{ color: "text.disabled" }}>{icon}</Icon>
                      <Typography
                        fontSize={16}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        {title}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{
                        alignSelf: { xs: "flex-start", sm: "end" },
                        color: "#1a7a3f",
                        fontWeight: 600,
                      }}
                    >
                      {`$${numberWithCommas(total || 0)}`}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <Grid container mt={headerless ? 0 : 4} spacing={2} px={(!headerless && isMobile) ? 1 : 0}>
                    {children}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {headerless && (
            <Grid container spacing={2} px={1}>
              {children}
            </Grid>
          )}
        </Paper>
      </Box>
    </SectionMobileContext.Provider>
  );
}
